import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const HlsVerticalPlayer = ({
  src,
  isVisible,
  isMuted,
  playing,
  onEnded,
  onPlay,
  onPause,
  poster,
  id,
  style,
  videoRef: externalVideoRef,
  showControls = false,
  subtitles = [],
  autoFullscreen = false
}) => {
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const hasRequestedAutoFullscreenRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [buffering, setBuffering] = useState(false);

  const isIOS = typeof navigator !== 'undefined' && /iP(hone|od|ad)/.test(navigator.userAgent);

  useEffect(() => {
  const video = videoRef.current;
    if (!video || !src) return;

    setReady(false);

    // allow loading external VTT files across origins when possible
    try { video.crossOrigin = 'anonymous'; } catch (e) {}

    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({
        maxBufferLength: 30,
        maxBufferHole: 1,
        fragLoadingTimeOut: 20000,
        enableWorker: true,
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
      video._hls = hls;
    } else {
      video.src = src;
    }

    const enableFirstTextTrack = () => {
      try {
        const tracks = video.textTracks;
        if (tracks && tracks.length > 0) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('HlsVerticalPlayer: found textTracks', tracks.length);
            for (let i = 0; i < tracks.length; i++) {
              console.debug(' track', i, tracks[i]);
            }
          }
          for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = i === 0 ? 'showing' : 'disabled';
          }
          return true;
        }
      } catch (e) { }
      return false;
    };

    const handleLoaded = () => {
      setReady(true);
      setBuffering(false);
      enableFirstTextTrack();
    };

    const handleLoadedMetadata = () => {
      enableFirstTextTrack();
    };
    const handlePlaying = () => {
      setReady(true);
      setBuffering(false);
    };
    const handleWaiting = () => {
      setBuffering(true);
    };
    const handleStalled = () => {
      setBuffering(true);
    };

    video.addEventListener('loadeddata', handleLoaded);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('playing', handlePlaying);

    if(!isIOS) {
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('stalled', handleStalled);
    }

    const handleAddTrack = () => { enableFirstTextTrack(); };
    try {
      if (video.textTracks && video.textTracks.addEventListener) {
        video.textTracks.addEventListener('addtrack', handleAddTrack);
      }
    } catch (e) {}

    return () => {
      video.removeEventListener('loadeddata', handleLoaded);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('playing', handlePlaying);

      if(!isIOS) {
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('stalled', handleStalled);
      }

      try {
        if (video.textTracks && video.textTracks.removeEventListener) {
          video.textTracks.removeEventListener('addtrack', handleAddTrack);
        }
      } catch (e) {}
      
      try {
        if (hls) { hls.destroy(); }
        if (video && video._hls) { video._hls.destroy(); delete video._hls; }
      } catch (e) { console.debug('destroy error', e); }
    };
  }, [src, subtitles]);

  useEffect(() => {
    const video = videoRef.current;
    if (!autoFullscreen || !video || hasRequestedAutoFullscreenRef.current || isIOS) return;

    hasRequestedAutoFullscreenRef.current = true;
    const requestFullscreen = video.requestFullscreen || video.webkitRequestFullscreen || video.mozRequestFullScreen || video.msRequestFullscreen;

    if (requestFullscreen) {
      const result = requestFullscreen.call(video);
      if (result?.catch) result.catch(() => {});
    }
  }, [autoFullscreen, isIOS]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !!isMuted;
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing && isVisible) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      try { video.pause(); } catch (e) {}
    }
  }, [playing, isVisible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (ready && playing && isVisible) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [ready, playing, isVisible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => onEnded && onEnded();
    const handlePlay = () => { onPlay && onPlay(); setReady(true); };
    const handlePause = () => { onPause && onPause(); };
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [onEnded, onPlay, onPause]);

  // Toggle objectFit when entering/exiting fullscreen so video is fully visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      if (fsEl) {
        try { video.style.objectFit = 'contain'; } catch (e) {  }
      } else {
        try { video.style.objectFit = 'cover'; } catch (e) {  }
      }
    };

    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange', handleFsChange);
    document.addEventListener('MSFullscreenChange', handleFsChange);

    handleFsChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange', handleFsChange);
      document.removeEventListener('MSFullscreenChange', handleFsChange);
      
      try { if (video) video.style.objectFit = 'cover'; } catch (e) {}
    };
  }, []);

  return (
    <div id={`hls-vertical-container-${id || ''}`} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          backgroundImage: (buffering || !ready) && poster ? `url(${poster})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: (buffering || !ready) && !poster ? '#000' : undefined,
        }}
        playsInline
        controls={showControls}
      >
        {Array.isArray(subtitles) && subtitles.map((sub, idx) => (
          sub && sub.subtitle_url ? (
            <track
              key={`sub-${idx}`}
              kind="subtitles"
              src={sub.subtitle_url}
              srcLang={sub.short_code || sub.code || ''}
              label={sub.language_name || sub.short_code || `Subtitle ${idx+1}`}
              default={idx === 0}
            />
          ) : null
        ))}
      </video>
      {buffering && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            background: 'rgba(0,0,0,0.25)'
          }}
        >
          <svg width="48" height="48" viewBox="0 0 50 50" aria-hidden="true">
            <circle cx="25" cy="25" r="20" fill="none" stroke="#ffffff" strokeWidth="4" strokeOpacity="0.25" />
            <circle cx="25" cy="25" r="20" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeDasharray="31.4 31.4">
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      )}
      {!ready && poster && (
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${poster})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 2 }} />
      )}
      {!ready && !poster && (
        <div style={{ position: 'absolute', inset: 0, background: '#000', zIndex: 2 }} />
      )}
    </div>
  );
};

export default HlsVerticalPlayer;
